import React, { useState } from 'react'
import { changePassword } from '../../lib/spektrix'

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState(null)
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required'
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required'
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long'
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setMessage(null)
    
    try {
      await changePassword(formData.currentPassword, formData.newPassword)
      setMessage({ type: 'success', text: 'Your password has been changed successfully.' })
      // Clear form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      console.error('Failed to change password:', error)
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to change password. Please check your current password and try again.' 
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-white">Change Password</h2>
      
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          <span className="text-white">{message.text}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div className="form-control">
          <label className="label">
            <span className="label-text text-gray-300">Current Password</span>
          </label>
          <div className="relative">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              className={`input input-bordered w-full pr-12 bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:outline-none ${errors.currentPassword ? 'input-error' : ''}`}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm btn-circle"
              onClick={() => togglePasswordVisibility('current')}
            >
              {showPasswords.current ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {errors.currentPassword && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.currentPassword}</span>
            </label>
          )}
        </div>
        
        <div className="form-control">
          <label className="label">
            <span className="label-text text-gray-300">New Password</span>
          </label>
          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              className={`input input-bordered w-full pr-12 bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:outline-none ${errors.newPassword ? 'input-error' : ''}`}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm btn-circle"
              onClick={() => togglePasswordVisibility('new')}
            >
              {showPasswords.new ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {errors.newPassword && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.newPassword}</span>
            </label>
          )}
          <label className="label">
            <span className="label-text-alt text-gray-400">Password must be at least 8 characters long</span>
          </label>
        </div>
        
        <div className="form-control">
          <label className="label">
            <span className="label-text text-gray-300">Confirm New Password</span>
          </label>
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`input input-bordered w-full pr-12 bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:outline-none ${errors.confirmPassword ? 'input-error' : ''}`}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm btn-circle"
              onClick={() => togglePasswordVisibility('confirm')}
            >
              {showPasswords.confirm ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {errors.confirmPassword && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.confirmPassword}</span>
            </label>
          )}
        </div>
        
        <div className="form-control mt-6">
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Changing Password...
              </>
            ) : (
              'Change Password'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ChangePassword